import React, { useEffect, useRef, useState } from "react";
import { api, type LanguageView } from "../api.js";
import { exportLabel } from "./languageView.js";

/**
 * The export button while a language is shown: what can be taken away in
 * that language. The video is made on the Export page; the rest are the
 * language's own files, offered only once they exist.
 */
export const LanguageExport: React.FC<{
  projectId: string;
  language: LanguageView;
  /** Goes to the Export page for this language's video. */
  onExportVideo: () => void;
}> = ({ projectId, language, onExportVideo }) => {
  const [open, setOpen] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const files = (
    [
      ["vtt", "Subtitles · .vtt"],
      ["srt", "Subtitles · .srt"],
      ["script", "Script · .txt"],
      ["audio", "Voice only · .mp3"],
    ] as const
  ).flatMap(([kind, label]) => {
    const href = language.downloads[kind];
    return href ? [{ kind, label, href }] : [];
  });

  const zip = async () => {
    setZipping(true);
    setError(null);
    try {
      const { url } = await api.languagesDownloadLink(projectId);
      setOpen(false);
      window.location.assign(url);
    } catch {
      setError("The download couldn't be prepared. Try again in a moment.");
    } finally {
      setZipping(false);
    }
  };

  const item =
    "flex w-full items-center justify-between rounded-[8px] px-3 py-2.5 text-left text-[13px] text-ink-100 transition-colors hover:bg-bay-800";

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        className="btn btn-primary"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {exportLabel(language)}
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="ml-1.5">
          <path d="M1.5 3.5 L5 7 L8.5 3.5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 flex w-[260px] flex-col rounded-[12px] border border-[var(--edge)] bg-bay-900 p-1.5 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className={`${item} disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent`}
            // Only a ready language can be exported: one still being voiced would be refused.
            disabled={language.status !== "ready"}
            title={language.status !== "ready" ? "Ready once it is voiced" : undefined}
            onClick={() => {
              setOpen(false);
              onExportVideo();
            }}
          >
            Video · MP4
          </button>
          {files.map((file) => (
            <a
              key={file.kind}
              role="menuitem"
              className={item}
              href={file.href}
              download
              onClick={() => setOpen(false)}
            >
              {file.label}
            </a>
          ))}
          <div className="my-1 h-px bg-[var(--edge)]" />
          <button
            type="button"
            role="menuitem"
            className={`${item} font-semibold !text-ember-600`}
            disabled={zipping}
            onClick={() => void zip()}
          >
            {zipping ? "Preparing…" : "All languages · .zip"}
          </button>
          {error ? <p className="px-3 py-1.5 text-[11px] leading-snug text-signal-bad">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
};
